import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase-config';

export default function PaymentCommissionManager() {
  const [transactions, setTransactions] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalCommissions: 0,
    pendingPayouts: 0,
    activeAffiliates: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load transactions
      const transactionsQuery = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
      const transactionsSnapshot = await getDocs(transactionsQuery);
      const transactionsData = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Load commissions
      const commissionsQuery = query(collection(db, 'commissions'), orderBy('createdAt', 'desc'));
      const commissionsSnapshot = await getDocs(commissionsQuery);
      const commissionsData = commissionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Load payouts
      const payoutsQuery = query(collection(db, 'payouts'), orderBy('createdAt', 'desc'));
      const payoutsSnapshot = await getDocs(payoutsQuery);
      const payoutsData = payoutsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      setTransactions(transactionsData);
      setCommissions(commissionsData);
      setPayouts(payoutsData);

      // Calculate stats
      const totalRevenue = transactionsData.reduce((sum, t) => sum + (t.amount || 0), 0);
      const totalCommissions = commissionsData.reduce((sum, c) => sum + (c.amount || 0), 0);
      const pendingPayouts = commissionsData
        .filter(c => c.status === 'pending')
        .reduce((sum, c) => sum + (c.amount || 0), 0);
      
      setStats({
        totalRevenue,
        totalCommissions,
        pendingPayouts,
        activeAffiliates: new Set(commissionsData.map(c => c.affiliateId)).size
      });

    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const processCommission = async (transactionId, affiliateId, amount, level = 1) => {
    try {
      const commissionRate = level === 1 ? 0.10 : level === 2 ? 0.05 : 0.02; // 10%, 5%, 2%
      const commissionAmount = amount * commissionRate;

      await addDoc(collection(db, 'commissions'), {
        transactionId,
        affiliateId,
        amount: commissionAmount,
        level,
        status: 'pending',
        createdAt: new Date(),
        type: 'affiliate_commission'
      });

      console.log(`Commission processed: ${commissionAmount} Ft for affiliate ${affiliateId}`);
    } catch (error) {
      console.error('Error processing commission:', error);
    }
  };

  const createPayout = async (affiliateId, amount) => {
    setLoading(true);
    try {
      // Create payout record
      await addDoc(collection(db, 'payouts'), {
        affiliateId,
        amount,
        status: 'pending',
        createdAt: new Date(),
        paymentMethod: 'bank_transfer',
        currency: 'HUF'
      });

      // Update commission statuses to 'paid'
      const affiliateCommissions = commissions.filter(
        c => c.affiliateId === affiliateId && c.status === 'pending'
      );

      for (const commission of affiliateCommissions) {
        await updateDoc(doc(db, 'commissions', commission.id), {
          status: 'paid',
          paidAt: new Date()
        });
      }

      setShowPayoutModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating payout:', error);
    }
    setLoading(false);
  };

  const getAffiliateCommissionTotal = (affiliateId) => {
    return commissions
      .filter(c => c.affiliateId === affiliateId && c.status === 'pending')
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'paid': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      case 'processing': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Függőben';
      case 'paid': return 'Kifizetve';
      case 'cancelled': return 'Törölve';
      case 'processing': return 'Feldolgozás alatt';
      default: return 'Ismeretlen';
    }
  };

  // Group commissions by affiliate
  const affiliateCommissions = commissions.reduce((acc, commission) => {
    if (!acc[commission.affiliateId]) {
      acc[commission.affiliateId] = [];
    }
    acc[commission.affiliateId].push(commission);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Fizetés & Jutalék Kezelő
        </h2>
        <button
          onClick={() => setShowPayoutModal(true)}
          className="bg-[#e31937] hover:bg-[#b91c1c] text-white px-4 py-2 rounded-lg flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Kifizetés Indítása
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Összes bevétel</p>
              <p className="text-white text-xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
              <p className="text-green-400 text-xs">+12% ez a hónap</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Összes jutalék</p>
              <p className="text-white text-xl font-bold">{formatCurrency(stats.totalCommissions)}</p>
              <p className="text-blue-400 text-xs">Affiliate program</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Függő kifizetések</p>
              <p className="text-white text-xl font-bold">{formatCurrency(stats.pendingPayouts)}</p>
              <p className="text-yellow-400 text-xs">Kifizetésre vár</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Aktív affiliate-ok</p>
              <p className="text-white text-xl font-bold">{stats.activeAffiliates}</p>
              <p className="text-purple-400 text-xs">Partner hálózat</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Affiliate Commissions Overview */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
        <h3 className="text-white font-semibold mb-4">Affiliate Jutalékok Áttekintése</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(affiliateCommissions).map(([affiliateId, commissions]) => {
            const pendingAmount = commissions
              .filter(c => c.status === 'pending')
              .reduce((sum, c) => sum + (c.amount || 0), 0);
            const totalAmount = commissions.reduce((sum, c) => sum + (c.amount || 0), 0);

            return (
              <div key={affiliateId} className="bg-[#2a2a2a] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">{affiliateId}</h4>
                  <span className="text-xs text-gray-400">{commissions.length} tranzakció</span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Függő:</span>
                    <span className="text-yellow-400 font-medium">{formatCurrency(pendingAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Összes:</span>
                    <span className="text-white font-medium">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
                {pendingAmount > 0 && (
                  <button
                    onClick={() => {
                      setSelectedAffiliate({ id: affiliateId, pendingAmount });
                      setShowPayoutModal(true);
                    }}
                    className="w-full mt-3 bg-[#e31937] hover:bg-[#b91c1c] text-white py-1 px-2 rounded text-sm"
                  >
                    Kifizetés
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-[#e31937]/20">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold">Legutóbbi Tranzakciók</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-[#2a2a2a]">
              <tr>
                <th className="px-4 py-3 text-left">Dátum</th>
                <th className="px-4 py-3 text-left">Tranzakció ID</th>
                <th className="px-4 py-3 text-left">Összeg</th>
                <th className="px-4 py-3 text-left">Típus</th>
                <th className="px-4 py-3 text-left">Státusz</th>
                <th className="px-4 py-3 text-left">Jutalék</th>
              </tr>
            </thead>
            <tbody>
              {transactions.slice(0, 10).map(transaction => {
                const relatedCommissions = commissions.filter(c => c.transactionId === transaction.id);
                const totalCommission = relatedCommissions.reduce((sum, c) => sum + (c.amount || 0), 0);

                return (
                  <tr key={transaction.id} className="border-b border-gray-700 hover:bg-[#2a2a2a]">
                    <td className="px-4 py-3">{transaction.createdAt.toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-mono text-sm">{transaction.id.slice(0, 8)}...</td>
                    <td className="px-4 py-3 font-medium">{formatCurrency(transaction.amount || 0)}</td>
                    <td className="px-4 py-3">{transaction.type || 'Vásárlás'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status || 'completed')} text-white`}>
                        {getStatusText(transaction.status || 'completed')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {totalCommission > 0 ? (
                        <span className="text-green-400 font-medium">{formatCurrency(totalCommission)}</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1a] rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Kifizetés Indítása</h3>
              <button
                onClick={() => {
                  setShowPayoutModal(false);
                  setSelectedAffiliate(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {selectedAffiliate ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Affiliate ID</label>
                  <p className="text-white font-medium">{selectedAffiliate.id}</p>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Kifizetendő összeg</label>
                  <p className="text-white text-2xl font-bold">{formatCurrency(selectedAffiliate.pendingAmount)}</p>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Fizetési mód</label>
                  <select className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white">
                    <option value="bank_transfer">Banki átutalás</option>
                    <option value="paypal">PayPal</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </div>

                <div className="flex space-x-2 pt-4">
                  <button
                    onClick={() => createPayout(selectedAffiliate.id, selectedAffiliate.pendingAmount)}
                    disabled={loading}
                    className="flex-1 bg-[#e31937] hover:bg-[#b91c1c] disabled:opacity-50 text-white py-2 px-4 rounded-lg"
                  >
                    {loading ? 'Feldolgozás...' : 'Kifizetés Jóváhagyása'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPayoutModal(false);
                      setSelectedAffiliate(null);
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                  >
                    Mégse
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <p>Válassz egy affiliate-t a kifizetéshez</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
